import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { StatService } from '@cargos/sprintpay-services';
import { BehaviorSubject, Observable, catchError, finalize, of, take } from 'rxjs';
import { environment } from 'src/environments/environment';

declare global {
    interface Window {
        ChatWidget?: {
            init(options: {
                platform: string;
                apiUrl?: string;
                loginUrl?: string;
                position?: 'bottom-right' | 'bottom-left';
                userName?: string | null;
            }): void;
        };
    }
}

@Injectable({
    providedIn: 'root',
})
export class SprintbotService {
    private static readonly SCRIPT_ID: string = 'sprintbot-widget-script';
    private static readonly STAT_TIMEOUT_MS: number = 2000;

    private readonly document: Document = inject(DOCUMENT);
    private readonly statService: StatService = inject(StatService);
    private readonly isLoadedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private isLoaded: boolean = false;
    private isEvaluatingGate: boolean = false;

    public readonly isLoaded$: Observable<boolean> = this.isLoadedSubject.asObservable();

    public isEnvironmentEnabled(): boolean {
        return environment.sprintbot.enabled && !environment.sprintbot.forceDisabled;
    }

    public initialize(): void {
        if (this.isLoaded || this.isEvaluatingGate) {
            return;
        }

        if (this.isScriptAlreadyInjected()) {
            this.setLoaded(true);
            return;
        }

        if (!this.isEnvironmentEnabled()) {
            return;
        }

        const gateName: string = environment.sprintbot.statsigGateName;
        if (!gateName) {
            this.injectScript();
            return;
        }

        this.isEvaluatingGate = true;

        this.statService
            .isFeatureEnabledWhenReady(gateName, this.isEnvironmentEnabled(), SprintbotService.STAT_TIMEOUT_MS)
            .pipe(
                take(1),
                catchError(() => of(this.isEnvironmentEnabled())),
                finalize(() => {
                    this.isEvaluatingGate = false;
                })
            )
            .subscribe((isEnabled: boolean) => {
                if (!isEnabled || this.isLoaded) {
                    return;
                }

                this.injectScript();
            });
    }

    private injectScript(): void {
        if (this.isScriptAlreadyInjected()) {
            this.setLoaded(true);
            return;
        }

        const script: HTMLScriptElement = this.document.createElement('script');
        script.id = SprintbotService.SCRIPT_ID;
        script.type = 'text/javascript';
        script.src = environment.sprintbot.scriptUrl;
        script.async = true;
        script.dataset['platform'] = 'sprintpay';
        script.dataset['authMode'] = 'storage';
        script.dataset['authTokenStorage'] = 'sessionStorage';
        script.dataset['authTokenKey'] = 'currentUser';
        script.dataset['authTokenEncoding'] = 'base64_raw';
        script.dataset['authTokenPath'] = 'authorizationToken';
        script.dataset['authRefreshStorage'] = 'sessionStorage';
        script.dataset['authRefreshKey'] = 'accessControl';
        script.dataset['authRefreshEncoding'] = 'base64_raw';
        script.dataset['authRefreshPath'] = 'refreshToken';

        script.onload = (): void => {
            window.ChatWidget?.init({
                platform: 'sprintpay',
                apiUrl: environment.sprintbot.apiUrl,
                loginUrl: 'javascript:document.getElementById("mock-login-btn")?.click()',
                position: 'bottom-right',
            });
            this.setLoaded(true);
        };

        this.document.head?.appendChild(script);
    }

    private setLoaded(isLoaded: boolean): void {
        this.isLoaded = isLoaded;
        this.isLoadedSubject.next(isLoaded);
    }

    private isScriptAlreadyInjected(): boolean {
        const scriptById: HTMLScriptElement | null = this.document.querySelector(`#${SprintbotService.SCRIPT_ID}`);

        if (scriptById) {
            return true;
        }

        const scriptBySource: HTMLScriptElement | null = this.document.querySelector(
            `script[src="${environment.sprintbot.scriptUrl}"]`
        );

        return Boolean(scriptBySource);
    }
}
